/**
 * Date helpers shared by the request pages (list and detail), in the
 * Europe/Paris business zone. Money and percentage helpers live in lib/format.ts.
 */

const PARIS = 'Europe/Paris';

/** "05/11/2026 18:19" in the Europe/Paris business zone (table cells). */
export function formatDateTimeShort(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const day = date.toLocaleDateString('fr-FR', {
    timeZone: PARIS,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString('fr-FR', {
    timeZone: PARIS,
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${day} ${time}`;
}

/** "18:07:58" in Europe/Paris. */
export function formatTimeSeconds(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleTimeString('fr-FR', {
    timeZone: PARIS,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** "2 octobre 2026" in Europe/Paris (dates written in a sentence). */
export function formatLongDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString('fr-FR', {
    timeZone: PARIS,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Calendar day "YYYY-MM-DD" of a timestamp in Europe/Paris. */
export function parisDayKey(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  // The sv-SE locale writes dates as YYYY-MM-DD.
  return date.toLocaleDateString('sv-SE', { timeZone: PARIS });
}

/** Number of days of a "YYYY-MM" month (0 when the month is malformed). */
export function daysInMonth(month: string): number {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) {
    return 0;
  }
  const [, year = '0', monthNumber = '0'] = match;
  return new Date(Date.UTC(Number(year), Number(monthNumber), 0)).getUTCDate();
}

/** Whole number in French ("1 234"). */
export function formatInt(value: number): string {
  return value.toLocaleString('fr-FR');
}

/** Shortened request id for the breadcrumb: "req_01m3ynynkt…6418". */
export function shortRequestId(id: string): string {
  return id.length > 20 ? `${id.slice(0, 14)}…${id.slice(-4)}` : id;
}
